import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { FailurePanel } from '@/shared/ui/FailurePanel';
import { Sections } from '@/shared/ui/Layout';
import { PageHeader } from '@/shared/ui/PageHeader';
import { LoadingState } from '@/shared/ui/States';

import { useConnections, useConnectionSummaries } from '@/entities/connection';

import { useConnectors } from '../api/connectors';
import { ConnectionCards } from './ConnectionCards';
import { RegisterSourceDialog } from './RegisterSourceDialog';

import { byAttention, departureFrom, harvestReading, tally } from '../model/listing';

/* The sources this deployment can reach, as a worklist.
 *
 * **Two reads, and the second one is allowed to fail on its own.** `useConnections` gives the rows;
 * `useConnectionSummaries` gives the live harvest progress the status column renders. A failed
 * summary read costs the progress and not the screen — letting one aggregate's availability blank a
 * list of sources would make a harvest-progress query decide whether an operator can see their
 * estate, and the rows carry four honest facts without it.
 *
 * **Registering is a dialog over the list, as the design draws it**, rather than a panel that replaced
 * the list. A finished registration lands on the new source's own page; a dialog closed part-way
 * leaves the list where it was, with a line confirming the source exists if it got that far.
 */
export function Connections(): ReactElement {
  const { t } = useTranslation();
  const connections = useConnections();
  const summaries = useConnectionSummaries();
  const connectors = useConnectors();
  const [registerOpen, setRegisterOpen] = useState(false);
  /* **The confirmation lives here rather than inside the dialog**, because closing the dialog
   * unmounts it and a message inside would go with it. Set as soon as the source exists, and shown
   * once the dialog is closed — a source registered and then left untested still exists, and the
   * list should say so. A name, never a credential. */
  const [justRegistered, setJustRegistered] = useState<string | null>(null);
  /* Set by a source's own page after it deleted that source — the page it happened on is gone, so
     the confirmation lands here. A name and a count, never a credential. */
  const departed = departureFrom(useLocation().state);

  /* Read once per render rather than per row, so every relative age on the screen is measured from
   * the same instant — two rows differencing against two clocks can report the same timestamp as
   * "59 minutes" and "1 hour". Not memoised: `new Date()` reads nothing from the query, so a
   * dependency array claiming otherwise was a lie eslint correctly refused, and a plain const
   * already gives exactly one instant per render. */
  const now = new Date();

  const byId = useMemo(() => {
    const map = new Map(
      (summaries.data?.items ?? []).map((entry) => [entry.connection_id, entry]),
    );
    return map;
  }, [summaries.data]);

  /* Sorted so the sources needing attention come first — which is what the lede promises, so it has
   * to be a mechanism rather than a sentence. Memoised because sorting on every render would reorder
   * rows mid-interaction. */
  const ordered = useMemo(
    () =>
      connections.data === undefined ? [] : [...connections.data.items].sort(byAttention),
    [connections.data],
  );

  const counts = connections.isSuccess ? tally(connections.data.items) : null;

  /* **Polled only while a harvest is running**, so a card's progress moves without a reload and an
     idle screen sends nothing. Four seconds because a harvest describes objects in batches and a
     faster poll would mostly re-read the same figures. */
  const harvesting = (summaries.data?.items ?? []).some((entry) => {
    const kind = harvestReading(entry).kind;
    return kind === 'running' || kind === 'starting';
  });
  const { refetch } = summaries;
  useEffect(() => {
    if (!harvesting) return undefined;
    const timer = window.setInterval(() => {
      void refetch();
    }, 4000);
    return () => {
      window.clearInterval(timer);
    };
  }, [harvesting, refetch]);

  return (
    <Sections>
      <PageHeader
        title={t('connections.title')}
        help={t('help.feature.connections')}
        lede={t('connections.subtitle')}
        actions={
          <Button
            tone="primary"
            aria-haspopup="dialog"
            onClick={() => {
              setRegisterOpen(true);
            }}
          >
            {t('connections.addSource')}
          </Button>
        }
      />

      <RegisterSourceDialog
        open={registerOpen}
        onClose={() => {
          setRegisterOpen(false);
        }}
        onRegistered={(connection) => {
          setJustRegistered(connection.name);
        }}
      />

      {departed !== null ? (
        <Alert tone="notice" title={t('connections.deleted.title')}>
          {departed.retired === 0
            ? t('connections.deleted.body', { name: departed.name })
            : t('connections.deleted.bodyWithDatasets', {
                name: departed.name,
                datasets: departed.retired,
              })}
        </Alert>
      ) : null}

      {justRegistered !== null && !registerOpen ? (
        <Alert tone="notice" title={t('connections.register.registeredTitle')}>
          {t('connections.register.registeredBody', { name: justRegistered })}
        </Alert>
      ) : null}

      {/* **The sentence comes from the status, not from this screen.** It used to be one hardcoded
          hint covering every failure — "check that the platform is running" — which an approver who
          does not hold `catalog.connection:read` was shown above a detail reading `read on
          catalog.connection was denied`. `FailurePanel` derives all three from the error. */}
      {connections.isError ? (
        <FailurePanel
          error={connections.error}
          subject={t('failure.subject.connections')}
          title={t('connections.readFailed')}
          detail={connections.error.message}
          onRetry={() => {
            void connections.refetch();
          }}
        />
      ) : null}

      {connections.isPending ? (
        <LoadingState rows={3} label={t('connections.loading')} />
      ) : null}

      {connections.isSuccess && ordered.length === 0 ? (
        <EmptyState
          title={t('connections.emptyTitle')}
          guidance={t('connections.emptyGuidance')}
          action={
            <Button
              tone="primary"
              onClick={() => {
                setRegisterOpen(true);
              }}
            >
              {t('connections.addSource')}
            </Button>
          }
        />
      ) : null}

      {connections.isSuccess && ordered.length > 0 && counts !== null ? (
        <>
          {/* The design's counts strip: four figures, each in its state's colour, in place of the
              section heading's sentence. A zero still renders — "0 failed" is the answer a reader
              scanning for trouble wants. */}
          <div className="lq-cn__counts">
            <span>
              <strong className="lq-cn__count">{ordered.length}</strong>
              {t('connections.card.countSources', { count: ordered.length })}
            </span>
            <span>
              <strong className="lq-cn__count" data-tone="ok">
                {counts.verified}
              </strong>
              {t('connections.card.countVerified')}
            </span>
            <span>
              <strong className="lq-cn__count" data-tone="error">
                {counts.failed}
              </strong>
              {t('connections.card.countFailed')}
            </span>
            <span>
              <strong className="lq-cn__count" data-tone="unknown">
                {counts.unverified}
              </strong>
              {t('connections.card.countNever')}
            </span>
          </div>

          <ConnectionCards
            connections={ordered}
            summaries={byId}
            profiles={connectors.data?.items}
            now={now}
          />

          {/* **Reported rather than silent**, because a failed summary read costs every card its
              harvest progress and dataset counts, and nothing would otherwise say so. */}
          {summaries.isError ? (
            <p className="lq-source__note">{t('connections.progressUnread')}</p>
          ) : null}
        </>
      ) : null}
    </Sections>
  );
}
